<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import apiClient from "@/apiClient";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  PlusIcon,
  CreditCardIcon,
} from "@heroicons/vue/24/solid";

dayjs.extend(relativeTime);

const router = useRouter();

const vehicles = ref([]);
const fuelCards = ref([]);
const loading = ref(false);
const loadingFuelCards = ref(false);
const error = ref("");
const showAddModal = ref(false);
const newVehicleReg = ref("");
const newVehicleTypeId = ref("");
const vehicleTypes = ref([]);
const registrationSearch = ref("");
const savingVehicle = ref(false);
const deletingVehicleId = ref(null);
const vehiclePhotos = ref({});
const requestedVehiclePhotoIds = new Set();
const isVehicleActive = (vehicle) =>
  vehicle?.active === true || vehicle?.active === 1 || vehicle?.active === "1";
const isFuelCardActive = (card) =>
  card?.active === true || card?.active === 1 || card?.active === "1";

const fetchVehicles = async () => {
  loading.value = true;
  try {
    const response = await apiClient.get("/api/vehicle", {
      params: { per_page: 250 },
    });
    const data = response.data?.data || response.data || [];
    vehicles.value = Array.isArray(data) ? data : [];
    vehicles.value.forEach((vehicle) => {
      loadVehiclePhoto(vehicle);
    });
  } catch (e) {
    console.error(e);
    error.value = "Unable to load fleet data.";
  } finally {
    loading.value = false;
  }
};

const fetchFuelCards = async () => {
  loadingFuelCards.value = true;
  try {
    const response = await apiClient.get("/api/fuel-card", {
      params: { per_page: 250 },
    });
    const data = response.data?.data || response.data || [];
    fuelCards.value = Array.isArray(data) ? data : [];
  } catch (e) {
    console.error(e);
    if (!error.value) {
      error.value = "Unable to load fuel card data.";
    }
  } finally {
    loadingFuelCards.value = false;
  }
};

const fetchVehicleTypes = async () => {
  const response = await apiClient.get("/api/vehicle-types");
  vehicleTypes.value = response.data ?? [];
};

const fetchDashboardData = async () => {
  error.value = "";
  await Promise.all([fetchVehicles(), fetchFuelCards(), fetchVehicleTypes()]);
};

const totalVehicles = computed(() => vehicles.value.length);
const inactiveVehicles = computed(
  () => vehicles.value.filter((vehicle) => vehicle.active === 0 || vehicle.active === false).length,
);
const totalFuelCards = computed(() => fuelCards.value.length);
const inactiveFuelCards = computed(
  () => fuelCards.value.filter((card) => !isFuelCardActive(card)).length,
);
const unassignedFuelCards = computed(
  () => fuelCards.value.filter((card) => !card.current_vehicle_id).length,
);
const fuelCardsExpiringSoonCount = computed(
  () => fuelCards.value.filter((card) => isFuelCardActive(card) && dueSoon(card.expiry_date, 45)).length,
);

const dueSoon = (date, days = 30) => {
  if (!date) return false;
  return dayjs(date).diff(dayjs(), "day") <= days;
};

const motDueSoonCount = computed(
  () => vehicles.value.filter((vehicle) => dueSoon(vehicle.motExpiryDate)).length,
);
const taxDueSoonCount = computed(
  () => vehicles.value.filter((vehicle) => dueSoon(vehicle.taxDueDate)).length,
);
const allVehicles = computed(() =>
  [...vehicles.value].sort((a, b) =>
    (a.registrationNumber || "").localeCompare(b.registrationNumber || "", undefined, {
      sensitivity: "base",
      numeric: true,
    }),
  ),
);
const filteredAllVehicles = computed(() => {
  const query = registrationSearch.value.trim().toLowerCase();
  if (!query) return allVehicles.value;
  return allVehicles.value.filter((vehicle) =>
    (vehicle.registrationNumber || "").toLowerCase().includes(query),
  );
});

const upcomingMOT = computed(() =>
  [...vehicles.value]
    .filter((vehicle) => isVehicleActive(vehicle) && vehicle.motExpiryDate)
    .sort((a, b) => dayjs(a.motExpiryDate).diff(dayjs(b.motExpiryDate)))
    .slice(0, 5),
);
const upcomingTax = computed(() =>
  [...vehicles.value]
    .filter((vehicle) => isVehicleActive(vehicle) && vehicle.taxDueDate)
    .sort((a, b) => dayjs(a.taxDueDate).diff(dayjs(b.taxDueDate)))
    .slice(0, 5),
);
const upcomingFuelCardExpiries = computed(() =>
  [...fuelCards.value]
    .filter((card) => isFuelCardActive(card) && card.expiry_date)
    .sort((a, b) => dayjs(a.expiry_date).diff(dayjs(b.expiry_date)))
    .slice(0, 4),
);

const formatRelative = (value) =>
  value ? dayjs(value).fromNow() : "n/a";

const getVehicleInitials = (registration) => {
  const normalized = String(registration || "")
    .replace(/[^a-z0-9]/gi, "")
    .toUpperCase();
  if (!normalized) {
    return "?";
  }
  return normalized.slice(0, 2);
};

const loadVehiclePhoto = async (vehicle) => {
  if (!vehicle?.id || !vehicle?.primary_photo) {
    return;
  }
  if (vehiclePhotos.value[vehicle.id] || requestedVehiclePhotoIds.has(vehicle.id)) {
    return;
  }
  requestedVehiclePhotoIds.add(vehicle.id);
  try {
    const { data } = await apiClient.get(`/api/vehicle/${vehicle.id}/photo`);
    if (data?.image) {
      vehiclePhotos.value = { ...vehiclePhotos.value, [vehicle.id]: data.image };
    }
  } catch (photoError) {
    console.error("Failed to load vehicle photo", photoError);
  }
};

const openAddVehicle = () => {
  newVehicleReg.value = "";
  newVehicleTypeId.value =
    vehicleTypes.value.find((type) => type.active && type.slug === "generic")?.id
    ?? vehicleTypes.value.find((type) => type.active)?.id
    ?? "";
  showAddModal.value = true;
};

const closeAddVehicle = () => {
  if (savingVehicle.value) return;
  showAddModal.value = false;
};

const addVehicle = async () => {
  if (!newVehicleReg.value || newVehicleReg.value.length < 3) {
    alert("Please enter a valid registration number");
    return;
  }
  if (!newVehicleTypeId.value) {
    alert("Choose a vehicle type.");
    return;
  }
  savingVehicle.value = true;
  try {
    await apiClient.post("/api/vehicle", {
      registrationNumber: newVehicleReg.value.toUpperCase(),
      vehicle_type_id: Number(newVehicleTypeId.value),
    });
    await fetchVehicles();
    showAddModal.value = false;
  } catch (e) {
    console.error(e);
    alert("Unable to create vehicle.");
  } finally {
    savingVehicle.value = false;
  }
};

const confirmDelete = async (vehicleId) => {
  if (!vehicleId) return;
  const target = vehicles.value.find((v) => v.id === vehicleId);
  if (!target) return;
  if (!confirm(`Delete vehicle ${target.registrationNumber}?`)) {
    return;
  }
  deletingVehicleId.value = vehicleId;
  try {
    await apiClient.delete(`/api/vehicle/${vehicleId}`);
    vehicles.value = vehicles.value.filter((v) => v.id !== vehicleId);
  } catch (e) {
    console.error(e);
    alert("Unable to delete vehicle.");
  } finally {
    deletingVehicleId.value = null;
  }
};

onMounted(fetchDashboardData);
</script>

<template>
  <div class="space-y-6">
    <section class="box p-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
      <div>
        <p class="text-sm text-slate-500 uppercase tracking-wide">
          Fleet Dashboard
        </p>
        <h1 class="text-2xl font-semibold text-slate-900 dark:text-white">
          Keep the fleet on the road
        </h1>
        <p class="text-sm text-slate-500">
          Monitor upcoming deadlines, track inactive vehicles, and add new vehicles with one click.
        </p>
      </div>
      <div class="flex flex-wrap gap-2">
        <button
          class="btn text-xs flex items-center gap-1"
          @click="fetchDashboardData"
          :disabled="loading || loadingFuelCards"
        >
          <ClockIcon class="h-4 w-4" />
          <span>Refresh</span>
        </button>
        <button
          class="btn accent text-xs flex items-center gap-1"
          @click="openAddVehicle"
        >
          <PlusIcon class="h-4 w-4" />
          <span>Add Vehicle</span>
        </button>
      </div>
    </section>

    <section class="grid gap-4 md:grid-cols-2 lg:grid-cols-4 auto-rows-fr">
      <div class="box p-4 flex flex-col gap-1 h-full">
        <p class="text-xs uppercase text-slate-500">Total vehicles</p>
        <p class="text-3xl font-semibold text-slate-900 dark:text-white">
          {{ totalVehicles }}
        </p>
      </div>
      <div class="box p-4 flex flex-col gap-1 h-full">
        <p class="text-xs uppercase text-red-500 flex items-center gap-1">
          <ExclamationTriangleIcon class="h-4 w-4" />
          Inactive
        </p>
        <p class="text-3xl font-semibold text-red-600">
          {{ inactiveVehicles }}
        </p>
      </div>
      <div class="box p-4 flex flex-col gap-1 h-full">
        <p class="text-xs uppercase text-amber-500 flex items-center gap-1">
          <ClockIcon class="h-4 w-4" />
          MOT due (30d)
        </p>
        <p class="text-3xl font-semibold text-amber-600">
          {{ motDueSoonCount }}
        </p>
      </div>
      <div class="box p-4 flex flex-col gap-1 h-full">
        <p class="text-xs uppercase text-amber-500 flex items-center gap-1">
          <ClockIcon class="h-4 w-4" />
          Tax due (30d)
        </p>
        <p class="text-3xl font-semibold text-amber-600">
          {{ taxDueSoonCount }}
        </p>
      </div>
    </section>

    <section class="box p-4">
      <header class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-center gap-2">
          <CreditCardIcon class="h-5 w-5 text-cyan-500" />
          <div>
            <p class="font-semibold text-slate-900 dark:text-white">Fuel cards</p>
            <p class="text-xs text-slate-500">Quick fuel card status and upcoming expiries.</p>
          </div>
        </div>
        <button
          class="btn text-xs"
          @click="router.push({ name: 'fuel-cards' })"
        >
          View Fuel Cards
        </button>
      </header>

      <div class="mt-4 grid gap-3 md:grid-cols-4">
        <div class="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50">
          <p class="text-xs uppercase text-slate-500">Total cards</p>
          <p class="text-2xl font-semibold text-slate-900 dark:text-white">{{ totalFuelCards }}</p>
        </div>
        <div class="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50">
          <p class="text-xs uppercase text-amber-600">Expiring (45d)</p>
          <p class="text-2xl font-semibold text-amber-600">{{ fuelCardsExpiringSoonCount }}</p>
        </div>
        <div class="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50">
          <p class="text-xs uppercase text-slate-500">Unassigned</p>
          <p class="text-2xl font-semibold text-slate-900 dark:text-white">{{ unassignedFuelCards }}</p>
        </div>
        <div class="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50">
          <p class="text-xs uppercase text-red-500">Inactive</p>
          <p class="text-2xl font-semibold text-red-600">{{ inactiveFuelCards }}</p>
        </div>
      </div>

      <div class="mt-4">
        <p class="mb-2 text-xs uppercase text-slate-500">Next card expiries</p>
        <div v-if="loadingFuelCards" class="text-sm text-slate-500">
          Loading fuel cards...
        </div>
        <div v-else class="grid gap-2 sm:grid-cols-2">
          <button
            v-for="card in upcomingFuelCardExpiries"
            :key="`fuel-card-expiry-${card.id}`"
            type="button"
            class="rounded-lg border border-slate-200 px-3 py-2 text-left transition-colors hover:bg-cyan-50 dark:border-slate-700 dark:hover:bg-cyan-900/20"
            @click="router.push({ name: 'fuel-cards-view', params: { id: card.id } })"
          >
            <p class="text-sm font-semibold text-slate-900 dark:text-white">
              {{ card.card_name || card.card_number }}
            </p>
            <p class="text-xs text-slate-500">
              {{ card.card_number }} · {{ dayjs(card.expiry_date).format("DD MMM YYYY") }} ({{ formatRelative(card.expiry_date) }})
            </p>
            <p class="text-xs text-slate-500">
              Vehicle: {{ card.current_vehicle?.registrationNumber || "Unassigned" }}
            </p>
          </button>
          <p
            v-if="!upcomingFuelCardExpiries.length"
            class="rounded-lg border border-slate-200 p-3 text-sm text-slate-500 dark:border-slate-700 sm:col-span-2"
          >
            No active fuel cards with expiry dates.
          </p>
        </div>
      </div>
    </section>

    <section class="grid gap-4 lg:grid-cols-2 auto-rows-fr">
      <div class="box p-0 overflow-hidden h-full flex flex-col">
        <header class="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <p class="font-semibold text-slate-800 dark:text-white">Next 5 MOT expiries</p>
          <CheckCircleIcon class="h-5 w-5 text-emerald-500" />
        </header>
        <div class="flex-1 overflow-auto">
          <table class="w-full text-sm">
            <thead class="text-xs uppercase text-slate-500 bg-slate-50 dark:bg-slate-900/30">
              <tr>
                <th class="text-left p-3">Vehicle</th>
                <th class="text-left p-3">Expiry</th>
                <th class="text-left p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="vehicle in upcomingMOT"
                :key="vehicle.id"
                class="border-t border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
                @click="router.push({ name: 'vehicles-view', params: { id: vehicle.id } })"
              >
                <td class="p-3 font-semibold uppercase">
                  {{ vehicle.registrationNumber }}
                </td>
                <td class="p-3 text-slate-600 dark:text-slate-300">
                  {{ dayjs(vehicle.motExpiryDate).format("DD MMM YYYY") }}
                  <span class="text-xs text-slate-500 block">
                    {{ formatRelative(vehicle.motExpiryDate) }}
                  </span>
                </td>
                <td class="p-3">
                  <span
                    class="px-2 py-0.5 text-xs rounded"
                    :class="dueSoon(vehicle.motExpiryDate) ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'"
                  >
                    {{ dueSoon(vehicle.motExpiryDate) ? "Due soon" : "OK" }}
                  </span>
                </td>
              </tr>
              <tr v-if="!upcomingMOT.length">
                <td colspan="3" class="p-4 text-sm text-slate-500 text-center">
                  No vehicles with upcoming MOT.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="box p-0 overflow-hidden h-full flex flex-col">
        <header class="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <p class="font-semibold text-slate-800 dark:text-white">Next 5 tax expiries</p>
          <CheckCircleIcon class="h-5 w-5 text-emerald-500" />
        </header>
        <div class="flex-1 overflow-auto">
          <table class="w-full text-sm">
            <thead class="text-xs uppercase text-slate-500 bg-slate-50 dark:bg-slate-900/30">
              <tr>
                <th class="text-left p-3">Vehicle</th>
                <th class="text-left p-3">Expiry</th>
                <th class="text-left p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="vehicle in upcomingTax"
                :key="`${vehicle.id}-tax`"
                class="border-t border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
                @click="router.push({ name: 'vehicles-view', params: { id: vehicle.id } })"
              >
                <td class="p-3 font-semibold uppercase">
                  {{ vehicle.registrationNumber }}
                </td>
                <td class="p-3 text-slate-600 dark:text-slate-300">
                  {{ dayjs(vehicle.taxDueDate).format("DD MMM YYYY") }}
                  <span class="text-xs text-slate-500 block">
                    {{ formatRelative(vehicle.taxDueDate) }}
                  </span>
                </td>
                <td class="p-3">
                  <span
                    class="px-2 py-0.5 text-xs rounded"
                    :class="dueSoon(vehicle.taxDueDate) ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'"
                  >
                    {{ dueSoon(vehicle.taxDueDate) ? "Due soon" : "OK" }}
                  </span>
                </td>
              </tr>
              <tr v-if="!upcomingTax.length">
                <td colspan="3" class="p-4 text-sm text-slate-500 text-center">
                  No vehicles with upcoming tax deadlines.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <section class="box">
      <header class="flex flex-col gap-3 mb-4 md:flex-row md:items-center md:justify-between">
        <h2 class="text-lg font-semibold text-slate-900 dark:text-white">All vehicles</h2>
        <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            v-model="registrationSearch"
            type="search"
            class="w-full sm:w-56 border border-slate-300 rounded px-3 py-2 text-sm bg-white text-slate-900 placeholder:text-slate-400 dark:bg-slate-800 dark:text-slate-100 dark:border-slate-700 dark:placeholder:text-slate-500"
            placeholder="Search registration"
          />
          <button class="btn text-xs flex items-center gap-1" @click="router.push({ name: 'vehicles-create' })">
            <PlusIcon class="h-4 w-4" />
            <span>Add Vehicle</span>
          </button>
        </div>
      </header>
      <div class="overflow-auto">
        <table class="w-full text-sm">
          <thead class="text-xs uppercase text-slate-500 bg-slate-50 dark:bg-slate-900/30">
            <tr>
              <th class="p-3 text-left">Vehicle</th>
              <th class="p-3 text-left">Type</th>
              <th class="p-3 text-left">MOT</th>
              <th class="p-3 text-left">Tax</th>
              <th class="p-3 text-left">Status</th>
              <th class="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="vehicle in filteredAllVehicles"
              :key="`${vehicle.id}-list`"
              class="border-t border-slate-200 dark:border-slate-800 transition-colors hover:bg-cyan-50 dark:hover:bg-cyan-900/30"
            >
              <td class="p-3 cursor-pointer" @click="router.push({ name: 'vehicles-view', params: { id: vehicle.id } })">
                <div class="flex items-center gap-3">
                  <div
                    class="h-10 w-10 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center overflow-hidden"
                  >
                    <img
                      v-if="vehiclePhotos[vehicle.id]"
                      :src="vehiclePhotos[vehicle.id]"
                      :alt="`Vehicle ${vehicle.registrationNumber}`"
                      class="h-full w-full object-cover"
                    />
                    <span v-else class="text-xs font-semibold">
                      {{ getVehicleInitials(vehicle.registrationNumber) }}
                    </span>
                  </div>
                  <span class="font-semibold uppercase">
                    {{ vehicle.registrationNumber }}
                  </span>
                </div>
              </td>
              <td class="p-3 text-slate-600 dark:text-slate-300">
                {{ vehicle.vehicle_type?.name || "Unassigned" }}
              </td>
              <td class="p-3">
                {{ vehicle.motExpiryDate ? dayjs(vehicle.motExpiryDate).format("DD MMM YYYY") : "n/a" }}
              </td>
              <td class="p-3">
                {{ vehicle.taxDueDate ? dayjs(vehicle.taxDueDate).format("DD MMM YYYY") : "n/a" }}
              </td>
              <td class="p-3">
                <span
                  class="px-2 py-0.5 text-xs rounded"
                  :class="vehicle.active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'"
                >
                  {{ vehicle.active ? "Active" : "Inactive" }}
                </span>
              </td>
              <td class="p-3 text-right">
                <button
                  class="btn text-xs"
                  :disabled="deletingVehicleId === vehicle.id"
                  @click="confirmDelete(vehicle.id)"
                >
                  {{ deletingVehicleId === vehicle.id ? "Deleting..." : "Delete" }}
                </button>
              </td>
            </tr>
            <tr v-if="!filteredAllVehicles.length && !loading">
              <td colspan="6" class="p-4 text-center text-sm text-slate-500">
                {{ registrationSearch ? "No vehicles match that registration." : "No vehicles found." }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>


    <p v-if="error" class="text-sm text-red-600">
      {{ error }}
    </p>

    <div
      v-if="showAddModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="closeAddVehicle"
    >
      <div class="bg-white dark:bg-slate-900 rounded shadow-lg w-full max-w-md p-6 space-y-4">
        <h2 class="text-xl font-semibold text-slate-900 dark:text-white">
          Add Vehicle
        </h2>
        <div>
          <label class="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">
            Registration Number
          </label>
          <input
            v-model="newVehicleReg"
            class="w-full border rounded px-3 py-2 uppercase"
            :disabled="savingVehicle"
            placeholder="e.g. AB12CDE"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">
            Vehicle Type
          </label>
          <select
            v-model="newVehicleTypeId"
            class="w-full border rounded px-3 py-2"
            :disabled="savingVehicle"
          >
            <option value="" disabled>Select a type</option>
            <option
              v-for="type in vehicleTypes.filter((entry) => entry.active)"
              :key="type.id"
              :value="type.id"
            >
              {{ type.name }}
            </option>
          </select>
        </div>
        <div class="flex justify-end gap-2">
          <button class="btn text-xs" @click="closeAddVehicle" :disabled="savingVehicle">
            Cancel
          </button>
          <button class="btn accent text-xs" @click="addVehicle" :disabled="savingVehicle">
            {{ savingVehicle ? "Saving..." : "Save" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

    
